"use client";
import { useCallback, useEffect, useState } from "react";

import { paymentApi, type SavedCard } from "@/api/services";
import { useAuth } from "@/store/auth";

type UseSavedCardsResult = {
  cards: SavedCard[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  removeCard: (id: string) => Promise<void>;
};

export function useSavedCards(): UseSavedCardsResult {
  const { isLoggedIn } = useAuth();
  const [cards, setCards] = useState<SavedCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setCards([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await paymentApi.listSavedCards();
      setCards(list);
    } catch (err) {
      console.warn("[useSavedCards] list failed", err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const removeCard = useCallback(
    async (id: string) => {
      // Optimistic remove.
      setCards((prev) => prev.filter((c) => c.id !== id));
      try {
        await paymentApi.deleteSavedCard(id);
      } catch (err) {
        await refresh();
        throw err;
      }
    },
    [refresh],
  );

  return { cards, loading, error, refresh, removeCard };
}
